// /companies — 公司抬头编辑
// v1.5 假设公司是 seed 的 3 个, 只能改不能新建/删除
// 改后影响: PI PDF / 客户列表默认币种 / 教练引用的公司名
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { Toast } from '../components/Toast';
import { type CompanyDetail, companyApi } from '../lib/api';

export const Route = createFileRoute('/companies')({
  component: CompaniesPage,
});

const CURRENCIES = ['USD', 'JPY', 'EUR', 'CNY', 'HKD'] as const;

function CompaniesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companyApi.list(),
  });

  if (isLoading) {
    return (
      <div>
        <PageHeader title="公司抬头" description="编辑三家公司的名称、税号、默认货币" />
        <p className="text-ink-3 text-sm">加载中…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <PageHeader title="公司抬头" />
        <p className="text-bad text-sm">加载失败: {(error as Error)?.message ?? '未知'}</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="公司抬头"
        description="改后会影响 PI PDF / 客户列表 / 教练引用"
        breadcrumb={
          <Link to="/" className="hover:text-ink-2 transition-colors">
            ← 返回
          </Link>
        }
      />

      <p className="text-xs text-ink-3 mb-4">
        v1.5 暂只支持编辑 (不能新增/删除), 三角架构 3 家公司是固定的.
      </p>

      <div className="space-y-4">
        {data.companies.map((c) => (
          <CompanyEditor
            key={c.id}
            id={c.id}
            flag={c.flag}
            country={c.country}
            isDefault={c.isDefault}
            onSaved={() => qc.invalidateQueries({ queryKey: ['companies'] })}
          />
        ))}
      </div>
    </div>
  );
}

interface CompanyEditorProps {
  id: string;
  flag: string;
  country: string;
  isDefault: boolean;
  onSaved: () => void;
}

function CompanyEditor({ id, flag, country, isDefault, onSaved }: CompanyEditorProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: async (): Promise<CompanyDetail> => {
      const list = await companyApi.list();
      const c = list.companies.find((x) => x.id === id);
      if (!c) throw new Error('Company not found');
      // 简化: list 已包含 name/country/code, 但没 taxId/defaultCurrency
      // 真实数据要从 GET /:id 取; v1.5 暂时返回 list 视图
      return {
        id: c.id,
        code: c.code,
        name: c.name,
        country: c.country,
        role: '',
        defaultCurrency: c.code === 'JP' ? 'JPY' : c.code === 'HK' ? 'HKD' : 'CNY',
        active: true,
      };
    },
  });

  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [currency, setCurrency] = useState<string>('USD');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) {
      setName(data.name);
      setTaxId(data.taxId ?? '');
      setCurrency(data.defaultCurrency);
    }
  }, [data]);

  const mut = useMutation({
    mutationFn: (patch: {
      name?: string;
      taxId?: string;
      defaultCurrency?: 'USD' | 'JPY' | 'EUR' | 'CNY' | 'HKD';
    }) => companyApi.update(id, patch),
    onSuccess: () => {
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (isLoading || !data) {
    return (
      <div className="bg-surface border border-line rounded-lg p-5 text-sm text-ink-3">
        加载 {flag} {country}…
      </div>
    );
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim().length < 1 || name.length > 100) return;
    mut.mutate({
      name: name.trim(),
      taxId: taxId.trim() || '',
      defaultCurrency: currency as 'USD' | 'JPY' | 'EUR' | 'CNY' | 'HKD',
    });
  }

  return (
    <section className="bg-surface border border-line rounded-lg p-5 animate-fade-up">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{flag}</span>
        <div>
          <h2 className="text-base font-semibold">{country} 法人</h2>
          <p className="text-xs text-ink-3">
            {data.code} · {data.role || 'sales_front'}
            {isDefault && ' · 默认公司'}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="公司名称 (1-100 字)">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 transition-colors"
            required
            aria-label="公司名称"
          />
        </Field>
        <Field label="税号 / 法人番号">
          <input
            type="text"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            maxLength={50}
            placeholder="例: 0300-01-169456"
            className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 transition-colors font-mono"
            aria-label="税号"
          />
        </Field>
        <Field label="默认货币">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full px-3 py-2 border border-line rounded text-sm bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/15 transition-colors"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>

        <div className="md:col-span-2 flex items-center justify-end gap-3 mt-1">
          {mut.isError && (
            <span className="text-xs text-bad">保存失败: {(mut.error as Error).message}</span>
          )}
          {saved && <span className="text-xs text-good">已保存</span>}
          <button
            type="submit"
            disabled={mut.isPending}
            className="px-4 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-accent-2 disabled:opacity-50 transition-colors"
          >
            {mut.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      </form>

      {mut.isSuccess && <Toast kind="success" message={`${flag} ${country} 已更新`} />}
      {mut.isError && <Toast kind="error" message={(mut.error as Error).message} />}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs text-ink-2 mb-1.5 block">{label}</span>
      {children}
    </div>
  );
}
